import {css} from 'styled-components';

import {layoutMixins} from './layout';

type TInputProps = {
  isInvalid?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  leftChildrenWidth?: number;
  rightChildrenWidth?: number;
};

const inputBaseStyle = css<TInputProps>`
  display: block;
  width: 100%;
  max-width: 100%;
  outline: none;
  border-style: solid;
  ${({
    theme: {branding, dimensions, spacing, borderRadius},
    leftChildrenWidth,
    rightChildrenWidth,
  }) => css`
    height: ${dimensions.controlHeightM}px;
    color: ${branding.content.primary};
    background-color: ${branding.backgrounds.primary};
    padding-right: calc(${spacing.s4} + ${rightChildrenWidth ?? 0}px);
    padding-left: calc(
      ${leftChildrenWidth ? spacing.s5 : spacing.s4} + ${leftChildrenWidth ?? 0}px
    );
    border-width: ${dimensions.borderWidth}px;
    border-radius: ${borderRadius.input};
    &::placeholder,
    ::placeholder {
      color: ${branding.content.tertiary};
    }
  `};
`;

const getInputStyle = ({
  disabled,
  readOnly,
  isInvalid,
  theme: {controlColors, branding, palette, boxShadow},
}: TTheme & TInputProps) => {
  if (disabled) {
    return css`
      background-color: ${branding.backgrounds.secondary};
      border-color: ${palette.grey100};
      color: ${branding.colors.disabled};
      ${controlColors.disabled}

      &::placeholder, ::placeholder {
        color: ${branding.colors.disabled};
      }

      &:hover {
        cursor: not-allowed;
      }
    `;
  }

  if (readOnly) {
    return css`
      cursor: auto;
      border-color: ${palette.grey100};
      ${controlColors.readOnly}

      &:focus, &:focus-within {
        box-shadow: ${boxShadow.input};
        ${controlColors.focus}
      }
    `;
  }

  if (isInvalid) {
    return css`
      border-color: ${palette.red400};
      ${controlColors.invalid}

      &:focus, &:focus-within {
        border-color: ${palette.red400};
      }
    `;
  }

  return css`
    border-color: ${palette.grey100};
    ${controlColors.default}

    &:focus, &:focus-within {
      box-shadow: ${boxShadow.input};
      ${controlColors.focus}
    }
  `;
};

const input = css`
  ${inputBaseStyle}
  ${getInputStyle}
`;

const formField = css`
  ${layoutMixins.column};
  width: 100%;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const formMixins = {formField, input} as const;
export type {TInputProps};
